<template>
    <div class="content">
        <Background>
            <Loading v-if="state === 'loading' || state === 'submitting'" isCenter isFull />
            <FriendlierCard v-else>
                <div class="padded-block">
                    <div class="logo">
                        <LocaleAwareLogo variant="primary" color="cloud" />
                    </div>

                    <!-- Not logged in -->
                    <div v-if="state === 'unauthenticated'" class="bin-section">
                        <div class="title">Log in to claim your returns and get your money back</div>
                        <p class="is-cloud description">You need to be logged in to confirm container returns at this bin.</p>
                        <div class="button-container">
                            <button class="button primary-button" @click="goToLogin">Log In</button>
                        </div>
                    </div>

                    <!-- Ready to submit -->
                    <div v-else-if="state === 'ready'" class="bin-section">
                        <div class="title">Bin Return</div>
                        <p v-if="dryRunError" class="error-message">{{ dryRunError }}</p>
                        <template v-else-if="previewCount > 0">
                            <p class="is-cloud description">
                                Tap to confirm your {{ previewCount }} pending return{{ previewCount === 1 ? '' : 's' }} from the past 24 hours.
                            </p>
                            <p v-if="previewAmount > 0" class="is-cloud description">
                                Estimated deposit: ${{ previewAmount.toFixed(2) }}
                            </p>
                        </template>
                        <p v-else class="is-cloud description">
                            No container scans found from the past 24 hours.
                        </p>
                        <div class="button-container">
                            <button class="button primary-button" @click="submit" :disabled="previewCount === 0 || !!dryRunError">Claim your containers</button>
                        </div>
                    </div>
                </div>
            </FriendlierCard>
        </Background>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AuthService } from "@/revamp/firebase/config";
import { Background, FriendlierCard, LocaleAwareLogo, Loading } from "@/revamp/components";
import { useFirebase } from "@/revamp/composables";
import { isIOS } from "@/revamp/utils/isMobile";

// How long to wait for the native app to take over before assuming it's not
// installed and falling back to the web flow. If the redirect below succeeds,
// the tab gets backgrounded almost immediately, well under this window.
const APP_REDIRECT_FALLBACK_MS = 1200;

const route = useRoute();
const router = useRouter();
const APIService = useFirebase();

const state = ref("loading");
const binUrl = ref("");
const previewCount = ref(0);
const previewAmount = ref(0);
const dryRunError = ref("");
let unsubscribeAuth = null;
let redirectFallbackTimer = null;
let webFlowStarted = false;

onMounted(() => {
    const binId = Array.isArray(route.params.binId) ? route.params.binId[0] : route.params.binId;
    binUrl.value = window.location.href;

    // Attempt to open the native app via its custom URL scheme.
    //
    // Skip this on iOS: a script-triggered navigation to a custom scheme
    // always makes iOS Safari show its own "Open in 'Friendlier'?"
    // confirmation — that's an OS-level restriction with no JS workaround.
    // Universal Links (see apple-app-site-association) are the only way to
    // open the app on iOS without that prompt, so we rely on those there
    // instead: silent when they work, a normal web page when they don't.
    //
    // On other platforms this fires immediately. If the app is installed,
    // the tab backgrounds almost instantly; if not, it silently no-ops and
    // we fall back to the web flow after a short grace period.
    if (isIOS()) {
        // Nothing to attempt here — Universal Links either already opened the
        // app before this page ever loaded, or they didn't and this is the
        // intended web fallback. Either way, start the web flow right away
        // rather than waiting out a grace period for a redirect we never fire.
        startWebFlow();
        return;
    }

    window.location.href = `friendlier://bin/${encodeURIComponent(String(binId))}`;

    document.addEventListener("visibilitychange", handleVisibilityChange);
    redirectFallbackTimer = setTimeout(() => {
        if (!document.hidden) {
            startWebFlow();
        }
    }, APP_REDIRECT_FALLBACK_MS);
});

function handleVisibilityChange() {
    // The tab regained focus (e.g. the redirect didn't actually open the app,
    // or the user came back on their own) and we haven't shown anything yet —
    // proceed with the web flow now instead of leaving the page stuck loading.
    if (!document.hidden && !webFlowStarted) {
        clearTimeout(redirectFallbackTimer);
        startWebFlow();
    }
}

function startWebFlow() {
    if (webFlowStarted) return;
    webFlowStarted = true;

    unsubscribeAuth = AuthService.onAuthStateChanged(async (user) => {
        unsubscribeAuth?.();
        unsubscribeAuth = null;

        if (!user) {
            state.value = "unauthenticated";
            return;
        }

        const preview = await APIService.scanReturnBin(binUrl.value, true);
        if (preview.error) {
            dryRunError.value = friendlyBinScanError(preview.message);
        } else {
            previewCount.value = preview.confirmedCodes?.length ?? 0;
            previewAmount.value = preview.amount ?? 0;
        }
        state.value = "ready";
    });
}

onUnmounted(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    clearTimeout(redirectFallbackTimer);
    unsubscribeAuth?.();
    unsubscribeAuth = null;
});

function friendlyBinScanError(message) {
    if (!message) return "Something went wrong. Please try again later.";
    if (message.includes("too many awaiting return scans")) {
        return "You have too many pending returns. Please wait for pending containers to be processed at a facility before scanning another bin.";
    }
    if (message.includes("Return Bin Not Found")) {
        return "This bin could not be found. Please try scanning again.";
    }
    return "Something went wrong. Please try again later.";
}

async function submit() {
    state.value = "submitting";
    const response = await APIService.scanReturnBin(binUrl.value);
    const binId = Array.isArray(route.params.binId) ? route.params.binId[0] : route.params.binId;

    if (response.error) {
        router.push({
            name: "BinScanResult",
            params: { binId },
            state: {
                resultState: "error",
                errorMessage: friendlyBinScanError(response?.message),
            },
        });
        return;
    }

    const confirmedCodes = response.confirmedCodes ?? [];
    router.push({
        name: "BinScanResult",
        params: { binId },
        state: {
            resultState: confirmedCodes.length === 0 ? "empty" : "success",
            confirmedCodes,
            amount: response.amount ?? 0,
        },
    });
}

function goToLogin() {
    router.push({ name: "Welcome", query: { redirect: route.fullPath } });
}
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";
@import "@/revamp/assets/scss/variables.scss";

.logo {
    max-width: 30%;
    min-width: 100%;
}

.bin-section {
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 15px;

    .title {
        font-size: 1.1rem;
        color: $sunflower;
        width: 100%;
        margin-bottom: 15px;
        text-align: center;
    }

    .description {
        text-align: center;
        margin-bottom: 5px;
    }

    .error-message {
        text-align: center;
        margin-bottom: 5px;
        color: $sunrise;
        font-size: 0.95rem;
    }

    .button-container {
        margin-top: 15px;

        .button {
            width: 100%;
            padding: 18px;
            border-radius: 10px;
            border: none;
            font-weight: bold;
            font-size: 1.1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            touch-action: manipulation;
        }

        .primary-button {
            background-color: $sunflower;
            color: $denim;

            &:active {
                background-color: darken($sunflower, 10%);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }
}
</style>